import XLSX from "sheetjs-style";
import * as FileSaver from "file-saver";
import {
    DEFAULT_ENTRIES_PER_PARTICIPANT,
    GRADE_SCHEMA,
    LEARN_MORE_LINK,
    LINK,
    QUESTION_TYPES,
    SAMPLE_QUESTIONS,
    USER_SETTINGS
} from "../../utils/constants";
import {
    getDefaultParticipationPoints,
    isParticipationPointEnabled,
    notifyError
} from "../../utils/helpers";
import React, {useEffect, useLayoutEffect, useState} from "react";
import { ReactComponent as UploadImage } from "../../images/upload_purple.svg";
import {ReactComponent as CLOSE} from "../../images/Close.svg"
import { ReactComponent as SearchIcon } from "../../images/search_bar_icon.svg";
import { ReactComponent as DELETE } from "./images/delete_icon.svg";
import {convertMHOptionMap, getBlanks, initialQuestionData, questionNameToCode} from "../../utils/questionUtils";
import {Button} from "@base-ui/react/button";
import classes from "./ActivityEditor.module.css";
import YuJaButton from "../../components/standardization/YuJaButton";
import QuestionAccordion from "./QuestionAccordion";
import OverflowText from "../../components/OverflowText";
import {YuJaTextBox} from "../../components/standardization/YuJaTextBox";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {convertToPlainString} from "../../components/Tiptap/latexParser";
import {ReactComponent as NoResultImage} from "../../images/no-result-found.svg";
import {YuJaTooltip} from "../../components/standardization/YuJaTooltip";
import { useTranslation } from "react-i18next";



export default function ImportQuestionsCSV({draftActivity, userSettings, gradeSchema, setImportedQuestions}) {
    const { t } = useTranslation();
    const {width: windowWidth} = useWindowDimensions();
    const [fileName, setFileName] = useState("");
    const [fileSize, setFileSize] = useState("");
    const [keyword, setKeyword] = useState("");
    const [validQuestions, setValidQuestions] = useState(0);
    const [invalidQuestions, setInvalidQuestions] = useState(0);
    const [fakePoll, setFakePoll] = useState(() => {
        let copy = JSON.parse(JSON.stringify(draftActivity));
        const {questions, ...rest} = copy;
        return {poll: rest, questions};
    });

    const [filteredPoll, setFilteredPoll] = useState(JSON.parse(JSON.stringify(fakePoll)));


    const fileReader = new FileReader();

    const handleImport = (list) => {
        // let questionsCopy = JSON.parse(JSON.stringify(questions));
        // const size = questionsCopy.length;
        // const lastQ = size ? questionsCopy[size - 1] : {};
        // if (!lastQ.questionType && !!list.length) {
        //     questionsCopy.pop();
        // }
        let curr_questions = [];
        list.forEach((item) => {
            const modifiedQuestion = {};
            modifiedQuestion.queTitle = item["Question Title"];
            modifiedQuestion.questionType = questionNameToCode(item["Question Type"]);
            const { optionsMap, correctAnswers } = initialQuestionData(questionNameToCode(item["Question Type"]), userSettings, false);
            modifiedQuestion.optionsMap = optionsMap;
            modifiedQuestion.correctAnswers = correctAnswers;
            if (item.Options && item["Question Type"] === QUESTION_TYPES.MCSS.description) {
                let MCoptionsMap = {};
                const optionsArray = item.Options.split(",").map(option => option.trim());
                const answerArr = !!item.Answer ? item.Answer.split(",").map(answer => answer.trim()) : [];
                optionsArray.forEach((option, index) => {
                    const key = String.fromCharCode(97 + index);
                    if (answerArr.includes(option)) {
                        modifiedQuestion.correctAnswers.push(key);
                    }
                    MCoptionsMap[key] = option;
                });
                modifiedQuestion.optionsMap = MCoptionsMap;
            }
            else if (item.Answer && item["Question Type"] === QUESTION_TYPES.TF.description) {
                if (item.Answer.toLowerCase() === "true" || item.Answer.toLowerCase() === "false" ) {
                    modifiedQuestion.correctAnswers = item.Answer;
                }
            }
            else if (item.Answer && item["Question Type"] === QUESTION_TYPES.MH.description) {
                modifiedQuestion.correctAnswers = transformMatchingString(item.Answer);
                modifiedQuestion.optionsMap = convertMHOptionMap(modifiedQuestion.correctAnswers);
            }
            else if (item["Blank Pairs"] && item["Question Type"] === QUESTION_TYPES.FITB.description) {
                modifiedQuestion.correctAnswers = transformFITBString(item["Blank Pairs"]);
                if (Object.entries(modifiedQuestion.correctAnswers).length === 0) {
                    let blanks = getBlanks(modifiedQuestion.queTitle, true);
                    const newCorrectAnswers = {};
                    for (const blankName of blanks) {
                        newCorrectAnswers[blankName] = "";
                    }
                    modifiedQuestion.correctAnswers = newCorrectAnswers;
                }

            } else if (item["Question Type"] === QUESTION_TYPES.RK.description && item.Options) {
                const optionsArray = item.Options.split(", ").map(option => option.trim());
                modifiedQuestion.optionsMap = optionsArray;
            } else if (item["Question Type"] === QUESTION_TYPES.WC.description) {
                modifiedQuestion.entries = userSettings && userSettings[USER_SETTINGS.QUESTION_ENTRIES]
                    ? userSettings[USER_SETTINGS.QUESTION_ENTRIES]
                    : DEFAULT_ENTRIES_PER_PARTICIPANT;
            }
            modifiedQuestion.weightage = gradeSchema === GRADE_SCHEMA.UNGRADED.key
                ? 0
                : userSettings[USER_SETTINGS.QUESTION_POINTS];
            if (isParticipationPointEnabled(userSettings)) {
                modifiedQuestion.participationWeightage = gradeSchema === GRADE_SCHEMA.UNGRADED.key
                    ? 0
                    : getDefaultParticipationPoints(userSettings);
            }

            modifiedQuestion.serialNo = curr_questions.length + 1;
            curr_questions.push(modifiedQuestion);
        });

        let copy = JSON.parse(JSON.stringify(draftActivity));
        const {questions, ...rest} = copy;
        copy.questions = curr_questions;
        setFakePoll({poll: rest, questions: curr_questions});
        setImportedQuestions(curr_questions);
        // notifySuccess(IMPORT_SUCCESS_MESSAGE);
        // handleClose();
    };

    const transformMatchingString = (input) => {
        try {
            const cleanedInput = input.slice(1, -1);

            const pairs = cleanedInput.split(',');

            const result = pairs.map(pair => {
                const [premise, response] = pair.split(':');
                return response ? [premise.trim(), response.trim()] : [premise.trim()];
            });

            return result;
        } catch (e) {
            throw new Error("invalid format");
        }
    };

    const transformFITBString = (input) => {
        try {
            const cleanedInput = input.slice(1, -1);

            const pairs = cleanedInput.split(',').map(i => i.trim());

            const result = pairs.reduce((acc, pair) => {
                const [blank, answers] = pair.split(':').map(i => i.trim());
                const answersArray = answers.split('|').map(i => i.trim())
                acc[blank.trim()] = answersArray;
                return acc;
            }, {});

            return result;
        } catch (e) {
            console.log("invalid format", e)
            return {};
        }
    };

    const validateRows = (rows) => {
        let invalidRowCount = 0;
        const validRows = [];
        rows.forEach(row => {
            let valid = true;
            // ... (keep your existing validation logic here)
            if (valid) {
                validRows.push(row);
            }
        });

        // setData(validRows);
        // setImportedData(validRows);
        setValidQuestions(validRows.length);
        setInvalidQuestions(rows.length - validRows.length);

        // Return both the count and the actual data
        return { validRows, count: validRows.length };
    };


    const handleDownloadTemplate = () => {
        const fileType = "application/octet-stream";
        const fileExtension = ".csv";
        const ws = XLSX.utils.json_to_sheet(SAMPLE_QUESTIONS);
        const csvOutput = XLSX.utils.sheet_to_csv(ws);
        function s2ab(s) {
            let buf = new ArrayBuffer(s.length);
            let view = new Uint8Array(buf);
            for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF;
            return buf;
        }

        const data = new Blob([s2ab(csvOutput)], { type: fileType });
        FileSaver.saveAs(data, "Sample File" + fileExtension);
    };

    const dragOver = (e) => {
        e.preventDefault();
    };

    const dragEnter = (e) => {
        e.preventDefault();
    };

    const dragLeave = (e) => {
        e.preventDefault();
    };

    const handleOnChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        fileReader.onload = function (event) {
            const text = event.target.result;
            const { validRows, count } = csvFileToArray(text);

            if (count === 0) {
                notifyError(t("questionBank.invalidCsvError"));
            } else {
                // setFile(selectedFile);
                setFileName(selectedFile.name);
                setFileSize(selectedFile.size);
                handleImport(validRows);
            }
        };
        fileReader.readAsText(selectedFile);
    };


    const fileDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length > 1) {
            notifyError(t("questionBuilder.uploadOneFileError"));
            return;
        }

        fileReader.onload = function (event) {
            const text = event.target.result;
            const { validRows, count } = csvFileToArray(text);

            if (count === 0) {
                notifyError(t("questionBank.invalidCsvError"));
            } else {
                // setFile(files[0]);
                setFileName(files[0].name);
                setFileSize(files[0].size);
                handleImport(validRows);
            }
        };
        fileReader.readAsText(files[0]);
    };


    const csvFileToArray = string => {
        const csvRows = string.split('\n');
        const csvHeader = parseCSVRow(csvRows[0]);

        const array = csvRows.slice(1, -1).map(row => {
            const values = parseCSVRow(row);
            const obj = csvHeader.reduce((object, header, index) => {
                object[header] = values[index];
                return object;
            }, {});
            return obj;
        });

        // setArray(array);
        // Get the rows from validateRows and return them
        const result = validateRows(array);
        return result;
    };

    const parseCSVRow = row => {
        const result = [];
        let inQuotes = false;
        let value = '';

        for (let i = 0; i < row.length; i++) {
            const char = row[i];
            if (char === '"' && row[i + 1] === '"') {
                // Handle escaped quotes within a quoted field
                value += '"';
                i++;
            } else if (char === '"') {
                // Toggle the inQuotes flag when encountering a quote character
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                // If not inside a quoted field, treat comma as field separator
                result.push(value.replaceAll("\r", "")
                    .replaceAll("\n", "")
                    .replaceAll("\t", ""));
                value = '';
            } else {
                // Otherwise, add the character to the current field
                value += char;
            }
        }
        result.push(value.replaceAll("\r", "")
            .replaceAll("\n", "")
            .replaceAll("\t", "")); // Add the last value
        return result;
    };


    useEffect(() => {
        const {poll={}, questions=[]} = fakePoll;
        let copyQuestions = JSON.parse(JSON.stringify(questions));
        setFilteredPoll({
            poll: JSON.parse(JSON.stringify(poll)),
            questions: copyQuestions.filter(q => convertToPlainString(q.queTitle).includes(keyword))
        });
    }, [fakePoll, keyword]);

    // useLayoutEffect(() => {
    //     if (document.activeElement === null || document.activeElement.tagName === "BODY") {
    //         let closeBtn = document.getElementById("import_question_close_icon");
    //         if (closeBtn) {
    //             closeBtn.focus();
    //         }
    //     }
    // }, []);


    if(!!fileName) {
        return (
            <div className={classes.column} style={{width:'100%', flex: 1}}>
                <div className={classes.fileUploadGridLayout}>
                    <div
                        className={classes.row}
                        style={{
                            gap: windowWidth > 600 ? 30 : 10,
                            fontSize: 15, fontWeight: '600',
                            width: '100%',
                        }}
                    >
                        {windowWidth > 600 ? t("questionBuilder.addedCsvFile") : t("questionBuilder.csvFile")}
                        <label tabIndex={0} htmlFor={"csvFileUploadInput"} className={classes.fileUploadInputContainer}>
                            <OverflowText placement={"top"}>
                                <span style={{flex: 1, minWidth: 0}}>
                                    {fileName}
                                </span>
                            </OverflowText>


                            <YuJaTooltip text={t("common.delete")}>
                                <YuJaButton
                                    iconOnly={true}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // setFile(null);
                                        setFakePoll({poll: {}, questions: []});
                                        setFileName("");
                                        setFileSize("");
                                    }}
                                >
                                    <DELETE/>
                                </YuJaButton>
                            </YuJaTooltip>

                        </label>

                        <input
                            id={"csvFileUploadInput"}
                            name={"csvFileUploadInput"}
                            type="file"
                            accept={".csv"}
                            onChange={handleOnChange}
                            aria-label={t("accessibility.uploadImage")}
                        />
                    </div>
                    <div/>
                    <YuJaTextBox
                        containerStyle={{
                            flex: 1,
                            height: 40,
                            minWidth: 100,
                            maxWidth: 600,
                            fontSize: 14,
                            lineHeight: 1.6,
                        }}
                        onChange={e => setKeyword(e.target.value)}
                        value={keyword}
                        before={<SearchIcon style={{marginRight: 5, width: 16}}/>}
                        after={
                            !!keyword
                                ? (
                                    <YuJaButton
                                        style={{width: 24, height: 24}}
                                        aria-label={t("accessibility.clearSearchKeyword")}
                                        iconOnly={true}
                                        onClick={() => setKeyword("")}
                                    >
                                        <CLOSE style={{width: 18, height: 18}}/>
                                    </YuJaButton>
                                )
                                : (<></>)
                        }
                        placeholder={t("report.activityTableSearchPlaceholder")}
                    />

                    <div className={classes.badge}>
                        {filteredPoll.questions.length} {t("table.questionsHeader")}
                    </div>
                </div>
                {filteredPoll.questions.length > 0
                    ? (<QuestionAccordion fakePoll={filteredPoll}/>)
                    : (
                        <div className={classes.column} style={{flex: 1, marginTop: 20, justifyContent: "center"}}>
                            <div className={classes.column} style={{alignItems: "center"}}>
                                <NoResultImage style={{height: 150, alignSelf: "center", margin: "20px 0"}}/>
                                <span style={{alignSelf: "center", fontSize: 20, fontWeight: "600", lineHeight: 1.4}}>
                                    {t("questionBuilder.noMatchingQuestions")}
                                </span>
                                <span style={{alignSelf: "center"}}>
                                    {t("questionBuilder.noMatchingQuestionsDesc")}
                                </span>
                            </div>
                        </div>
                    )
                }


            </div>
        );
    }


    return (
        <div className={classes.column} style={{maxWidth: 850, flex: 1}}>
            <div style={{fontSize: 14, marginTop: 15}}>
                {t("questionBank.uploadCsvFileText")}
                <Button
                    role={LINK}
                    aria-label={t("questionBuilder.learnMoreImportAria")}
                    className={classes.link}
                    style={{marginLeft: 5}}
                    onClick={() => window.open(LEARN_MORE_LINK)}
                > {t("common.learnMore")} </Button>
                .
            </div>

            <div style={{fontSize: 14, marginTop: 15}}>
                {t("questionBank.importQuestionModalBodyText")}
                <Button
                    role={LINK}
                    onClick={handleDownloadTemplate}
                    className={classes.link}
                    style={{marginLeft: 5}}
                >
                    {t("questionBuilder.downloadTemplate")}
                </Button>
                .
            </div>


            <div
                className={classes.fileDragDropContainer}
                onDragOver={dragOver}
                onDragEnter={dragEnter}
                onDragLeave={dragLeave}
                onDrop={fileDrop}
            >
                <UploadImage width={75} height={75} style={{marginBottom: 20}}/>
                <p style={{marginBottom: 10}}>{t("questionBuilder.dragDropCsv")}</p>
                <label
                    tabIndex={0}
                    className={classes.chooseFileButton}
                    for={"csvFileUploadInput"}
                > {t("institution.chooseFileText")} </label>

                <input
                    id={"csvFileUploadInput"}
                    name={"csvFileUploadInput"}
                    className="browse-box"
                    type="file"
                    accept={".csv"}
                    onChange={handleOnChange}
                    aria-label={t("accessibility.uploadImage")}
                />
            </div>
        </div>
    );
}